import React, { useEffect, useState } from 'react';
import { cAPI } from '../../../utils/cAPI';
import { unwrapPayload } from '../../../features/integrations/googleMerchant/utilities';
import DateTimeText from '../../../components/DateTimeText';

const ProcessWorkers = () => {
    const [status, setStatus] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [reason, setReason] = useState('Wyłączone ręcznie z panelu procesu');

    const loadStatus = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const result = await cAPI.exposedFetch({
                path: '/Admin/ProcessWorkers',
                httpMethod: 'GET'
            });

            setStatus(unwrapPayload(result));
        } catch (err) {
            setError(err?.message || 'Nie udało się pobrać statusu workerów.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadStatus();
    }, []);

    const updateAllWorkers = async enabled => {
        setIsSaving(true);
        setError(null);

        try {
            const result = await cAPI.exposedFetch({
                path: '/Admin/ProcessWorkers/All',
                httpMethod: 'PUT',
                jsonBody: {
                    enabled,
                    reason: enabled ? null : reason
                }
            });

            setStatus(unwrapPayload(result));
        } catch (err) {
            setError(err?.message || 'Nie udało się zmienić statusu workerów.');
        } finally {
            setIsSaving(false);
        }
    };

    const updateWorker = async (workerKey, enabled) => {
        setIsSaving(true);
        setError(null);

        try {
            const result = await cAPI.exposedFetch({
                path: `/Admin/ProcessWorkers/${encodeURIComponent(workerKey)}`,
                httpMethod: 'PUT',
                jsonBody: {
                    enabled,
                    reason: enabled ? null : reason
                }
            });

            setStatus(unwrapPayload(result));
        } catch (err) {
            setError(err?.message || `Nie udało się zmienić statusu workera ${workerKey}.`);
        } finally {
            setIsSaving(false);
        }
    };

    const workers = status?.workers ?? [];
    const enabledCount = workers.filter(worker => worker.enabled).length;

    return (
        <div className="animated fadeIn">
            <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                    <div>
                        <i className="fa fa-cogs mr-2" />
                        <strong>Workery procesu</strong>
                    </div>
                    <button type="button" className="btn btn-sm btn-outline-primary" disabled={isLoading || isSaving} onClick={loadStatus}>
                        Odśwież
                    </button>
                </div>

                <div className="card-body">
                    <div className="alert alert-warning">
                        To ustawienie działa tylko w bieżącym procesie backendu. Nie zapisuje się w bazie i zresetuje się po restarcie aplikacji.
                        Jeśli produkcja ma kilka replik, trzeba wejść na tę stronę dla każdej repliki osobno.
                    </div>

                    {error && <div className="alert alert-danger">{error}</div>}
                    {status?.message && <div className="alert alert-info">{status.message}</div>}

                    <div className="row mb-3">
                        <div className="col-md-4">
                            <label className="font-weight-bold">Proces</label>
                            <div>PID: {status?.processId ?? '-'}</div>
                            <div>Maszyna: {status?.machineName ?? '-'}</div>
                            <div>Czas serwera: <DateTimeText value={status?.serverUtcNow} /></div>
                            <div>
                                Domyślny stan nowych workerów:{' '}
                                <span className={`badge ${status?.defaultWorkerEnabled ? 'badge-success' : 'badge-danger'}`}>
                                    {status?.defaultWorkerEnabled ? 'włączone' : 'wyłączone'}
                                </span>
                            </div>
                        </div>
                        <div className="col-md-8">
                            <label className="font-weight-bold">Powód wyłączenia</label>
                            <input
                                type="text"
                                className="form-control"
                                value={reason}
                                onChange={event => setReason(event.target.value)}
                                disabled={isSaving}
                            />
                            <small className="form-text text-muted">
                                Powód zostanie zapisany tylko w pamięci tego procesu i pokazany na tej stronie.
                            </small>
                        </div>
                    </div>

                    <div className="border rounded p-3 mb-4">
                        <div className="d-flex flex-wrap justify-content-between align-items-center">
                            <div className="mr-3 mb-2">
                                <h5 className="mb-1">Akcje zbiorcze</h5>
                                <div className="text-muted">
                                    Zmieniają indywidualny stan każdego zarejestrowanego workera w tym procesie.
                                </div>
                                <div className="small text-muted mt-1">
                                    Włączone: {enabledCount} / {workers.length}
                                </div>
                            </div>
                            <div className="mb-2">
                                <button
                                    type="button"
                                    className="btn btn-outline-success mr-2"
                                    disabled={!status || isSaving || workers.length === 0}
                                    onClick={() => updateAllWorkers(true)}
                                >
                                    Włącz wszystkie
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-outline-danger"
                                    disabled={!status || isSaving || workers.length === 0}
                                    onClick={() => updateAllWorkers(false)}
                                >
                                    Wyłącz wszystkie
                                </button>
                            </div>
                        </div>
                    </div>

                    <h5>Workery</h5>

                    {isLoading && <div>Ładowanie...</div>}

                    {!isLoading && workers.length === 0 && (
                        <div className="text-muted">Brak zarejestrowanych workerów w bieżącym procesie.</div>
                    )}

                    {workers.length > 0 && (
                        <div className="table-responsive">
                            <table className="table table-striped table-bordered">
                                <thead>
                                    <tr>
                                        <th>Worker</th>
                                        <th>Status</th>
                                        <th>Ostatnia rejestracja</th>
                                        <th>Powód</th>
                                        <th style={{ width: 160 }}>Akcja</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {workers.map(worker => (
                                        <tr key={worker.workerKey}>
                                            <td>
                                                <div className="font-weight-bold">{worker.displayName || worker.workerKey}</div>
                                                <div className="text-muted small">{worker.workerKey}</div>
                                                {worker.description && <div className="small">{worker.description}</div>}
                                            </td>
                                            <td>
                                                <span className={`badge ${worker.enabled ? 'badge-success' : 'badge-danger'}`}>
                                                    {worker.enabled ? 'włączony' : 'wyłączony'}
                                                </span>
                                            </td>
                                            <td><DateTimeText value={worker.lastRegisteredAtUtc} /></td>
                                            <td>{worker.disabledReason || '-'}</td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className={`btn btn-sm ${worker.enabled ? 'btn-outline-danger' : 'btn-outline-success'}`}
                                                    disabled={isSaving}
                                                    onClick={() => updateWorker(worker.workerKey, !worker.enabled)}
                                                >
                                                    {worker.enabled ? 'Wyłącz' : 'Włącz'}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProcessWorkers;
